import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';

import { noop } from 'lodash'

import { Field } from '@ui/blocks/Field'
import { InputLabel } from '@ui/blocks/Form/InputLabel'
import {
  DangerButton,
  PrimaryButton,
  TertiaryButton,
} from '@ui/elements/Button'
import { Divider } from '@ui/elements/Divider'
import { Space } from '@ui/elements/Space'
import { Text } from '@ui/elements/Text'
import { TextField } from '@ui/elements/TextField'

export interface IConfirmationFormProps {
  /** Type of the confirmation */
  type?: 'warning' | 'danger'
  /**
   * @deprecated Prefer using the content prop, which only wraps in a <Text> tag if passed a string
   * The context text of the confirmation modal
   */
  message?: ReactNode
  /** The context text or component of the confirmation modal **/
  content?: ReactNode | string
  /** Allow the user to input additional text into a text area */
  confirmationMessageRequired?: boolean
  withTextarea?: boolean
  /** Label of the text area */
  textareaLabel?: string
  /** Placeholder of the text area */
  textareaPlaceholder?: string
  /** The text of the confirm button */
  confirmButtonText?: string
  /** The text of the cancel button */
  cancelButtonText?: string
  handleConfirm?: (done: () => void, confirmationMessage: string) => void
  handleDismiss?(e: MouseEvent<HTMLElement>): void
}

export const ConfirmationForm = ({
  type = 'warning',
  message = 'Are you sure you want to proceed?',
  content,
  confirmationMessageRequired = false,
  withTextarea = false,
  textareaLabel = 'Message',
  textareaPlaceholder = 'Write a message',
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  handleConfirm = noop,
  handleDismiss = noop,
}: IConfirmationFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmationMessage, setConfirmationMessage] = useState('')

  const isConfirmationMessageEmpty = confirmationMessage.trim().length === 0

  const renderConfirmButton = () => {
    const ButtonComponent = type === 'warning' ? PrimaryButton : DangerButton

    return (
      <ButtonComponent
        isLoading={isSubmitting}
        onClick={onConfirm}
        disabled={
          isSubmitting ||
          (confirmationMessageRequired && isConfirmationMessageEmpty)
        }
        // Remove this when Catalog.steps.tsx is fully migrated please :pray:
        data-testid='btn-confirm'
      >
        {confirmButtonText}
      </ButtonComponent>
    )
  }

  const onConfirm = () => {
    const done = () => {
      setIsSubmitting(false)
    }

    setIsSubmitting(true)

    handleConfirm(done, confirmationMessage)
  }

  return (
    <Space size='large'>
      {!content && message && <Text>{message}</Text>}
      {typeof content === 'string' ? <Text>{content}</Text> : content}

      {withTextarea && (
        <div>
          <Field>
            <InputLabel
              htmlFor='confirmation-message'
              required={confirmationMessageRequired}
            >
              {textareaLabel}
            </InputLabel>
            <TextField
              value={confirmationMessage}
              onChange={(e) => setConfirmationMessage(e.target.value)}
              id='confirmation-message'
              multiline
              minRows={3}
              placeholder={textareaPlaceholder}
              fullWidth
            />
          </Field>
        </div>
      )}

      <Divider space={0} />

      <Space direction='horizontal' justify='end'>
        <TertiaryButton disabled={isSubmitting} onClick={handleDismiss}>
          {cancelButtonText}
        </TertiaryButton>

        {renderConfirmButton()}
      </Space>
    </Space>
  )
}

ConfirmationForm.displayName = 'ConfirmationForm'
